import React from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import CtaOne from "@components/CTA/CtaOne";
import { BASE_ASSETS_URL } from "@constants/constants";
import DataCard from "@components/Cards/DataCard";
import CardWithImage from "@components/Cards/CardWithImage";
import Slider from "@components/Slider";
import MiniCards from "@components/Cards/MiniCards";
import ClientsTestimonial from "@components/Cards/ClientsTestimonial";
import { BasicChip } from "@components/MiniComponents";
import { IoMdRepeat } from "react-icons/io";
import { MdOutlineGroups, MdOutlineSecurity } from "react-icons/md";
import { IoMedalOutline, IoRocketOutline } from "react-icons/io5";
import { MdOutlineElectricBolt } from "react-icons/md";
import BlogCard from "@components/Blog/BlogCard";
import Subscribe from "@components/Subscribe";
import ProfileCard from "@components/Cards/ProfileCard";
import { teamData } from "@constants/teamData";
import { createUTMQueryParams } from "@utils/signature_owner";

import { TbUserHeart } from "react-icons/tb";
import { AiOutlineThunderbolt } from "react-icons/ai";
import { GoLightBulb } from "react-icons/go";
import { PiGlobeLight, PiUsersThree } from "react-icons/pi";
import { ArrowRight } from "lucide-react";
const cardData = [
  {
    heading: "300k+",
    subHeading: "Our Global Client",
  },
  {
    heading: "15Y+",
    subHeading: "Total Experience",
  },
  {
    heading: "15+",
    subHeading: "Passionate People",
  },
  {
    heading: "99%",
    subHeading: "Customer Satisfaction",
  },
];

const whyChooseData = [
  {
    title: "Create Signature Forms",
    description: `Easily add signature fields to your forms using our intuitive form editor. Save time managing 100s of signatures without the hassle of creating individual documents.`,
    image: "choose1.png",
  },
  {
    title: "Team up, without thinking about cost",
    description:
      "Even if we are a platform for small businesses to take e-signatures, we have enterprise-grade security to protect your contracts from potential threats and ensure compliance with international and industry-specific eSignature laws.",
    image: "choose2.png",
  },
  {
    title: "PDF templates for repeating contracts",
    description:
      "When you have a contract that is 95% the same but has some minor changes to be made to send to every new person, avoid the redundancy of recreating and remapping signature fields, customize minor details, and send to new recipients efficiently.",
    image: "choose3.png",
  },
];

const customerImages = [
  "company1.png",
  "company2.png",
  "company3.png",
  "company4.png",
  "company5.png",
  "company6.png",
  "company7.png",
];

const miniCardsData = [
  {
    icon: <IoMdRepeat />,
    title: "Streamline Repetitive Operations",
    description:
      "Save time by automating routine tasks for scaling your small business.",
  },
  {
    icon: <IoRocketOutline />,
    title: "Accelerate Decision Making",
    description:
      "Speed up your business contracts for quicker decisions and enhanced agility.",
  },
  {
    icon: <MdOutlineGroups />,
    title: "Enhance Team Collaboration",
    description:
      "Facilitate better teamwork with features designed for easy collaboration.",
  },
];
const values = [
  {
    icon: MdOutlineSecurity,
    title: "Security is non-negotiable",
    description:
      "We deal with sensitive data. Security, compliance, and reliability aren’t features, they’re our foundation.",
  },
  {
    icon: TbUserHeart,
    title: "Customer first",
    description:
      "We win when our customers win. We listen closely, solve real problems, and evolve with your needs.",
  },
  {
    icon: AiOutlineThunderbolt,
    title: "Speed over perfection",
    description:
      "Fast, focused execution beats overthinking. We ship, learn, and improve—on repeat.",
  },
  {
    icon: IoMedalOutline,
    title: "Raise the standard",
    description:
      "We challenge ourselves to exceed what’s expected. We keep learning, pushing ourselves, and striving to exceed even our own highest standards of quality.",
  },
  {
    icon: GoLightBulb,
    title: "Act like owners",
    description:
      "We take responsibility, drive action, and energize everyone around us. Leadership is a habit, not a title.",
  },
  {
    icon: PiGlobeLight,
    title: "Think long-term, act local",
    description:
      "We build for the world, but we pay attention to the local context—whether that’s law, language, or people.",
  },
  {
    icon: PiUsersThree,
    title: "Win as one team",
    description:
      "We’re in it together. We help each other across roles and departments, because great things happen when no one works in silos.",
  },
];
const officeAddresses = [
  {
    location: "Delaware",
    address: "1111B S Governors Ave STE 34146 Dover, DE 19904, U.S.A",
  },
  {
    location: "Dubai",
    address:
      "001 - 58669, A1 - 3641379065, IFZA Business Park, Dubai Digital Park, Dubai Silicon Oasis, Dubai, U.A.E",
  },
  {
    location: "Pune ",
    address:
      "201, Sunshine Business Park, PCMC, Pune- 411027, Maharashtra, India ",
  },
];

const About = () => {
  return (
    <div className="w-full md:mt-20 mt-8">
      <Head>
        <title>BoloSign - AI for Smart Contract Management | About Us</title>
        <meta
          name="description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign " />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:url" content="https://www.bolosign.com/about/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management | About Us"
        />
        <meta
          property="og:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="bolosign.com" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/about/"
        />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management  | About Us"
        />
        <meta
          name="twitter:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link rel="canonical" href="https://www.bolosign.com/about/" />
      </Head>
      {/* hero section  */}
      <div className="flex flex-col justify-center items-center gap-28 xxl:gap-24 px-4 md:px-8 pb-24">
        <div className="max-w-screen-lg text-center">
          <CtaOne
            heading1={`BoloSign - AI for Smart Contract Management`}
            paragraph={`At BoloSign, our dedicated team is passionate about empowering small businesses with secure and user-friendly eSignature solutions. By building strong client relationships and continuously improving our platform, we are driving the future of digital signatures.`}
            paragraphStyling={`max-w-[900px] mx-auto`}
            buttonsStyling={`justify-center`}
            buttonPrimaryText={`Get Started`}
            buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON${createUTMQueryParams(
              {
                from: "WEBSITE_HERO_BUTTON",
              },
            )}`}
            buttonSecondaryText={`Buy Now`}
            buttonSecondaryUrl={`/signature/pricing`}
            newTab={true}
          />
        </div>
        {/* <div className="w-full xl:w-[1080px] relative flex justify-center items-center">
          <div className="w-5/6">
            <Image
              src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
              width={10}
              height={10}
              className={`rotateObject w-[38px] sm:w-[50px] xl:w-[80px] absolute -top-20 xl:-top-32 right-[30%] xl:right-0`}
              alt="hero"
            />
            <Image
              src={`https://assets-global.website-files.com/65dd9f1fc1354b976d7c7cea/65e5ac954cdd5d3914a6104b_about-hero-image.jpg`}
              width={10}
              height={10}
              className={`rounded-lg w-full`}
              alt="hero"
            />
          </div>
          <Image
            src={require("@assets/landing-page/hero1.png")}
            width={10}
            height={10}
            className={`moveCornerLeftObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute -top-12 sm:-top-20 lg:-top-32 left-0 xl:-left-12 z-10 -rotate-12`}
            alt="hero"
          />

          <Image
            src={require("@assets/landing-page/hero4.png")}
            width={10}
            height={10}
            className={`moveCornerDownObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute bottom-4 right-0 xxl:-right-28 z-10`}
            alt="hero"
          />
        </div> */}
      </div>

      {/* NVIDIA Inception Program Card */}
      <div className="w-full max-w-screen-xl mx-auto px-4 md:px-8 mb-16 lg:mb-20">
        <div className="bg-gradient-to-r bg-dark rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200 to-blue-200 rounded-full opacity-20 -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-200 to-pink-200 rounded-full opacity-20 translate-y-12 -translate-x-12"></div>

          <div className="relative z-10">
            {/* NVIDIA Logo */}
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                <Image
                  src={require("assets/announcements/nvidia-logo.png")}
                  alt="NVIDIA Logo"
                  className="h-12 w-auto object-contain"
                  width={48}
                  height={48}
                />
              </div>
            </div>

            {/* Main heading */}
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Proudly Backed by NVIDIA Inception
            </h2>

            {/* Content */}
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl font-semibold text-slate-400 mb-6">
                Now, here's what many don't know…
              </p>
              <p className="text-base md:text-lg text-slate-200 leading-relaxed mb-8">
                BoloSign is part of the{" "}
                <span className="font-semibold text-green-700">
                  NVIDIA Inception Program
                </span>
                , the global accelerator for AI-first startups. That membership
                delivers early access to NVIDIA's AI tools, expert guidance, and
                a stamp of confidence. It's a powerful signal for our users that
                you're using tech validated by one of the most respected names
                in AI.
              </p>

              {/* CTA Button */}
              <div className="flex justify-center">
                <a
                  href="/announcement/nvidia-inception-program"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-slate-800 text-white font-semibold rounded-xl hover:from-green-700 hover:to-slate-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Read Full Announcement
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* data cards section  */}
      <div className="w-full max-w-screen-xl mx-auto px-4 md:px-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {cardData.map((data, index) => (
          <DataCard
            key={index}
            heading={data.heading}
            subHeading={data.subHeading}
          />
        ))}
      </div>

      {/* why choose  */}
      {/* <div className="max-w-screen-mxl py-20 lg:py-28 mx-auto px-4 md:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-10">
        <div className="w-full sm:w-[80%] md:w-[70%] lg:w-full mx-auto flex flex-col justify-center items-center gap-12 sm:gap-28">
          <div className="w-full flex flex-col lg:flex-row justify-center items-center gap-20">
            <div className="flex-1 relative">
              <CtaOne
                heading2={`With the only eSignature Solution made for Small Businesses`}
                paragraph={`We understand the needs of your small business. That’s why we provide a centralized platform that enhances the efficiency of signatures in contracts, agreements, and other legal documents crucial to your business operations at a very affordable price.`}
                buttonPrimaryText={`Get Started`}
                buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON`}
              />
              <Image
                src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                width={10}
                height={10}
                className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
                alt="hero"
              />
            </div>
            <div className="flex-1 relative w-[90%] sm:w-full">
              <Image
                src={require("@assets/landing-page/only_solution1.png")}
                width={10}
                height={10}
                className={`rounded-lg w-full`}
                alt="hero"
              />
              <Image
                src={require("@assets/landing-page/only_solution2.png")}
                width={10}
                height={10}
                className={`imageBorders moveDownObject rounded-lg absolute -left-5 sm:-left-10 bottom-10 w-[130px] sm:w-[200px]`}
                alt="hero"
              />
            </div>
          </div>
          <div className="w-full flex flex-col-reverse lg:flex-row justify-center items-center gap-20">
            <div className="flex-1">
              <CardWithImage
                title={whyChooseData[0].title}
                description={whyChooseData[0].description}
                image={whyChooseData[0].image}
              />
            </div>
            <div className="flex-1 relative">
              <CtaOne
                heading2={`BoloSign - Why Choose Us`}
                paragraph={`We allow you to efficiently manage document workflows, send multi-recipient signatures, and receive real-time updates, significantly streamlining your operations. Each transaction is secure and legally compliant, backed by robust security features and a detailed audit trail.`}
                buttonPrimaryText={`Watch Trailer`}
                buttonPrimaryUrl={`https://www.youtube.com/watch?v=HE4oDN_T12Q`}
              />
              <Image
                src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                width={10}
                height={10}
                className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
                alt="hero"
              />
            </div>
          </div>
          <div className="w-full flex flex-col lg:flex-row justify-center gap-12 sm:gap-20">
            <CardWithImage
              title={whyChooseData[1].title}
              description={whyChooseData[1].description}
              image={whyChooseData[1].image}
            />
            <CardWithImage
              title={whyChooseData[2].title}
              description={whyChooseData[2].description}
              image={whyChooseData[2].image}
            />
          </div>
        </div>
      </div> */}
      {/* slider  */}
      {/* <div className="w-full">
        <Slider
          heading="5,000+ Companies Use BoloSign Around the World"
          sliderImagesData={customerImages}
          styling={`bg-red-100/75 py-16 border-y-2 border-gray-900`}
        />
      </div> */}
      {/* section 3 */}
      {/* <div className="max-w-screen-mxl pt-20 lg:pt-40 mx-auto px-4 md:px-8 xxl:px-0">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          <div className="sm:w-[83%] lg:w-[50%]">
            <CtaOne heading2={`Affordable eSignature solution for your team`} />
          </div>
          <div className="sm:w-[83%] lg:w-[45%] relative">
            <CtaOne
              paragraph={`Experience how easy document management can be with BoloSign. Our user-friendly platform is designed to help your team work better together, speeding up projects and enhancing productivity.`}
              buttonPrimaryText={`Get Started`}
              buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON`}
            />
            <Image
              src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
              width={10}
              height={10}
              className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
              alt="hero"
            />
          </div>
        </div>
        <div className="mt-20 w-full flex flex-col lg:flex-row gap-12 items-center justify-between">
          <div className="md:w-3/4 lg:flex-1 relative">
            <Image
              src={require("@assets/landing-page/section3.png")}
              width={10}
              height={10}
              className={`rounded-lg w-full`}
              alt="hero"
            />
            <div className="absolute bottom-6 left-6">
              <BasicChip
                icon={<MdOutlineElectricBolt />}
                text="Increase Productivity With BoloSign Signature"
              />
            </div>
          </div>
          <div className="md:w-3/4 lg:flex-1 flex justify-center lg:justify-end items-center">
            <MiniCards
              cardsData={miniCardsData}
              cardsStyling={
                "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 lg:gap-10"
              }
            />
          </div>
        </div>
      </div> */}
      {/* clients  */}
      {/* <div className="pt-20 lg:pt-40 flex flex-col gap-6 relative max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0">
        <ClientsTestimonial />
      </div> */}
      {/* our team  */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-8  pt-20 lg:pt-32 flex flex-col gap">
        <div className="max-w-screen-md mx-auto text-center px-4 relative">
          <CtaOne
            heading2="Team Behind BoloSign"
            // paragraph="We understand that effective for your task management is the cornerstone of productivity and project success."
            paragraphStyling={`w-full max-w-screen-sm mx-auto`}
          />
          {/* <div className="w-full bg-gray-200 relative">
            <Image
              src={`${BASE_ASSETS_URL}/v2/arrow.svg`}
              width={10}
              height={10}
              className={`moveUpObject w-[50px] sm:w-[80px] absolute xl:-top-40 xl:-left-32`}
              alt="hero"
            />
            <Image
              src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
              width={10}
              height={10}
              className={`rotateObject w-[50px] sm:w-[80px] absolute -top-8 xl:-top-20 right-0 xl:-right-32`}
              alt="hero"
            />
          </div> */}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3   gap-10 mt-12 sm:mt-28 xl:mt-10 mb-10 w-full">
          {teamData.map((team, index) => (
            <ProfileCard key={index} {...team} />
          ))}
        </div>
      </div>
      {/* articles  */}
      {/* <div className="w-full max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0 py-20 lg:py-28 flex flex-col gap">
        <div className="text-center max-w-screen-md mx-auto">
          <CtaOne
            heading2={`Take a Look at Our Featured Articles`}
            paragraph={`These articles will guide you on how to simplify office work, boost your efficiency, and concentrate on expanding your business.`}
            paragraphStyling={`max-w-[600px] mx-auto`}
          />
        </div>
        <div className="mt-10 flex flex-col md:flex-row gap-6 sm:max-w-[80%] md:max-w-full mx-auto">
          {blogCards.map((card, index) => (
            <BlogCard key={index} {...card} imageWidth="w-full md:h-[290px] xl:h-[330px]" />
          ))}
        </div>
      </div> */}
      {/* Our Values */}
      <div className="bg-surface-light py-24">
        <h1 className="text-center md:text-5xl text-4xl md:px-0 px-4 font-semibold leading-snug mb-12">
          Our 7 core values
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-screen-xl md:px-8 px-4 mx-auto relative">
          {values.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 md:h-[460px] h-auto flex justify-start items-start flex-col col-span-1"
            >
              <div>
                <item.icon width={60} height={60} className="text-[54px]" />
                <h3 className="text-[32px] leading-snug font-bold mb-2 min-h-[72px] mt-4">
                  {item.title}
                </h3>
              </div>
              <p className="text-gray-700 font-medium text-[20px] leading-[28px] mt-4">
                {item.description}
              </p>
            </div>
          ))}

          {/* Special bottom-right image block */}
          <div className="hidden lg:flex lg:row-start-2 lg:row-span-2 lg:col-start-3 lg:col-span-1 self-end w-full h-full rounded-2xl overflow-hidden bg-white justify-center items-center relative">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/about/values.png`}
              alt="BoloSign Values"
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>
      {/* Offices */}
      <div className="w-full pt-24">
        <div className="max-w-screen-xl mx-auto md:px-8 px-4 flex justify-center items-start gap-16 lg:flex-row flex-col-reverse">
          <div className="lg:w-2/3 w-full p-4 md:p-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/about/locations.png`}
              alt="hero"
              width={100}
              height={100}
              className="rounded-2xl object-cover w-full h-full"
            />
          </div>

          <div className="lg:w-1/3  w-full p-4 md:p-0">
            <h1 className="text-5xl font-semibold">Our Offices</h1>
            {officeAddresses?.map((address, index) => (
              <div
                className={`${
                  index === 0 ? "mt-8" : " border-t border-gray-300"
                } py-4 flex justify-start items-start gap-2`}
                key={index}
              >
                <span className="w-4 h-4 rounded-full bg-primary mt-2"></span>
                <div className="flex-1">
                  <h2 className="font-semibold text-[22px] leading-[26px]">
                    {address.location}
                  </h2>
                  <p className="text-[18px] leading-[24px] mt-2">
                    {address.address}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* subscribe  */}
      {/* <div className="w-full">
        <Subscribe />
      </div> */}
    </div>
  );
};

export default About;
